import React, { useEffect, useRef, useState } from "react";
import ImagePreloader from "../../models/imagePreloader";
import NavigatorPosition from "../../models/navigatorPosition";
import ImageNotAvailableImage from "../../assets/images/image-not-available-wide.png";
import ImageFrame from "../../models/imageFrame";
import Navigable, { NavigatorMove } from "../../models/navigable";
import Navigator from "../../models/navigator";
import LogService from "../../services/logService";
import LogDirectory from "../../services/logDirectory";
import imageService from "../../services/imageService";
import ImageCacheItem from "../../models/imageCacheItem";
import ProjectInfo from "../../models/projectInfo";
import Settings from "../../models/settings";
import ImageHd from "../../assets/images/image-hd.png";
import Tooltip from "../common/Tooltip";
import imageHelper from "../../util/imageHelper";
import featureService from "../../services/featureService";
import Feature from "../../models/feature";

const logService = new LogService(LogDirectory.EnableCameraLogging);

const Camera: React.FC<{
  cameraName: string;
  navigator: Navigator;
  project: ProjectInfo;
  settings: Settings;
}> = ({ cameraName, navigator, project, settings }) => {
  const [debugMessage, setDebugMessage] = useState<string>("Debug");

  const [imageUrl, setImageUrl] = useState<string>("");
  const [imageOriginalUrl, setImageOriginalUrl] = useState<string>("");
  const [showCameraOrginalUrl, setShowCameraOrginalUrl] =
    useState<boolean>(false);

  const [isLoadFullSizeImage, setIsLoadFullSizeImage] =
    useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const [navigable, setNavigable] = useState<Navigable>();

  useEffect(() => {
    logService.log(`camera[${cameraName}]:init()`);

    let isLoadFullSizeImageInternal: boolean = false;

    if (
      featureService.isFeatureEnabled(
        Feature.AutoProgressiveImageLoading,
        project,
      )
    ) {
      isLoadFullSizeImageInternal = true;
    }

    const imagePreloader: ImagePreloader = new ImagePreloader();

    let currentIdSession: number | null = null;
    let currentBatchImageFrames: ImageFrame[] = [];
    let currentBatchImageFrameIndex: number = -1;

    const navigable = new Navigable();
    navigable.id = `camera[${cameraName}]`;
    setNavigable(navigable);

    navigable.function1 = () => {
      // console.log(`camera[${cameraName}]:navigable.function1()`);
      isLoadFullSizeImageInternal = true;

      if (isLoadFullSizeImageInternal && !navigator.isPlaying) {
        loadFullSizeImage();
      }
    };

    let noImages: boolean = false;

    const onSetShowCameraOrginalUrl = (checked: boolean) => {
      setShowCameraOrginalUrl(checked);
    };

    settings.showCameraImageUrlObservable.attach(onSetShowCameraOrginalUrl);

    const loadData = async (navigatorPosition: NavigatorPosition) => {
      logService.log(`camera[${cameraName}]:loadData()`);
      try {
        const { data: sessionImageFrameIndex } =
          await imageService.getImageFrameIndex(
            project.idProject,
            navigatorPosition.idSession,
            navigatorPosition.distance,
            cameraName,
          );

        let startIndex =
          sessionImageFrameIndex - imageService.preloaderBackCacheSize * 10;
        if (startIndex < 0) {
          startIndex = 0;
        }
        const stopIndex = startIndex + imageService.batchSize;

        const { data: frames } = await imageService.getImageFrames(
          project.idProject,
          navigatorPosition.idSession,
          cameraName,
          startIndex,
          stopIndex,
        );

        currentBatchImageFrames = frames;

        currentBatchImageFrameIndex =
          NavigatorPosition.getClosestNavigatorPositionIndex(
            navigatorPosition,
            currentBatchImageFrames,
          );

        noImages = frames.length === 0;

        if (noImages) {
          setImageUrl(ImageNotAvailableImage);
          return;
        }

        var imageUrls = [];
        for (const frame of frames) {
          imageUrls.push(frame.imageUrl);
        }

        imagePreloader.setCameraName(cameraName);
        imagePreloader.setImageUrls(imageUrls);

        logService.log(`camera[${cameraName}]:loadData()-done`);
      } catch (error) {
        logService.log(error);
      }
    };

    const navigatorMove: NavigatorMove = async (
      navigatorPosition: NavigatorPosition,
    ) => {
      logService.log(`camera[${cameraName}]:navigator:move()`);

      if (currentIdSession === navigatorPosition.idSession) {
        // console.log("currentBatchImageFrames", currentBatchImageFrames);

        currentBatchImageFrameIndex =
          NavigatorPosition.getClosestNavigatorPositionIndex(
            navigatorPosition,
            currentBatchImageFrames,
          );
      } else {
        currentBatchImageFrameIndex = -1;
        currentIdSession = navigatorPosition.idSession;
        noImages = false;
      }

      if (currentBatchImageFrameIndex === -1 && !noImages) {
        await loadData(navigatorPosition);
      }

      if (noImages) {
        return;
      }

      const imageUrl = await imagePreloader.getImage(
        currentBatchImageFrameIndex,
      );

      if (imageUrl && imageUrl !== ImageCacheItem.LOADING) {
        setImageUrl(imageUrl);
        setImageOriginalUrl(
          imagePreloader.getImageOriginalUrl(currentBatchImageFrameIndex),
        );

        if (isLoadFullSizeImageInternal && !navigator.isPlaying) {
          loadFullSizeImage();
        }
      }

      logService.log(`camera[${cameraName}]:navigator:move()-done`);
    };
    navigable.navigatorMove = navigatorMove;

    const playbackIsPlayingChanged = (isPlaying: boolean) => {
      if (isLoadFullSizeImageInternal && !isPlaying) {
        loadFullSizeImage();
      }
    };
    navigable.playbackIsPlayingChanged = playbackIsPlayingChanged;

    const loadFullSizeImage = () => {
      // console.trace();

      if (currentBatchImageFrameIndex === -1) {
        return;
      }

      const imageUrlToLoad =
        currentBatchImageFrames[currentBatchImageFrameIndex].imageFullSizeUrl;

      setTimeout(() => {
        const currentImageUrlBeforeLoad =
          currentBatchImageFrames[currentBatchImageFrameIndex].imageFullSizeUrl;

        if (imageUrlToLoad !== currentImageUrlBeforeLoad) {
          setDebugMessage(`[${cameraName}] FullSize image load abort.`);
          return;
        }

        setDebugMessage(`[${cameraName}] FullSize image loading...`);

        imageHelper
          .loadImage(imageUrlToLoad)
          .then((image) => {
            setDebugMessage(`[${cameraName}] FullSize image loading done.`);

            const currentImageUrlAfterLoad =
              currentBatchImageFrames[currentBatchImageFrameIndex]
                .imageFullSizeUrl;

            if (imageUrlToLoad !== currentImageUrlAfterLoad) {
              setDebugMessage(`[${cameraName}] FullSize image set abort.`);
              return;
            }

            setDebugMessage(`[${cameraName}] FullSize image set.`);
            setImageUrl(image.src);
          })
          .catch((error) => {
            setDebugMessage(
              `[${cameraName}] FullSize image load error ${error}`,
            );
          });
      }, 100);
    };

    // const navigatorMoveAllCompleted: NavigatorMoveAllCompleted = async () => {
    //   logService.log(`camera[${cameraName}]:MoveAllCompleted`);

    //   if (imageFrameIndex !== -1) {
    //     imagePreloader.preloadImages(imageFrameIndex);
    //   }
    // };
    // navigable.navigatorMoveAllCompleted = navigatorMoveAllCompleted;

    navigator.attach(navigable);

    // this sould be called on open/close window
    if (
      navigator.navigatorPosition &&
      navigator.navigatorPosition.idProject === project.idProject
    ) {
      logService.log(`camera[${cameraName}]:navigator:move()-init`);
      navigatorMove(navigator.navigatorPosition);
    }

    return () => {
      navigable.function1 = undefined;
      navigator.detach(navigable);
      imagePreloader.clearCache();
      settings.showCameraImageUrlObservable.detach(onSetShowCameraOrginalUrl);
    };
  }, [cameraName, navigator, project, settings]);

  const onHdImageLoad = async () => {
    setIsLoadFullSizeImage(true);

    navigable?.function1();
  };

  return (
    <div className="camera-img-container" ref={containerRef}>
      <div style={{ display: "none" }}>{debugMessage}</div>
      {showCameraOrginalUrl && (
        <div
          style={{
            position: "absolute",
            background: "white",
            opacity: "0.8",
            zIndex: 1,
          }}
        >
          {imageOriginalUrl}
        </div>
      )}

      <div
        className="d-flex flex-column"
        style={{ position: "absolute", top: "4px", left: "4px" }}
      >
        {!featureService.isFeatureEnabled(
          Feature.AutoProgressiveImageLoading,
          project,
        ) && (
          <Tooltip
            title="Hi-Res"
            placement="right"
            parentContainer={containerRef}
          >
            <div
              className={
                "image-button" +
                (isLoadFullSizeImage ? " image-button-active" : "")
              }
              style={{ marginBottom: "4px" }}
              onClick={onHdImageLoad}
            >
              <img style={{ height: "18px" }} src={ImageHd} alt="" />
            </div>
          </Tooltip>
        )}
      </div>

      {imageUrl && <img className="camera-img" src={imageUrl} alt="" />}
    </div>
  );
};

export default Camera;
